import { ClickScrollPlugin, OverlayScrollbars } from 'overlayscrollbars'
import 'overlayscrollbars/overlayscrollbars.css'
import { IS_LOCAL } from './constants'

// `scrollbars.clickScroll` is a no-op unless this plugin is registered: the track-click branch of OS's pointerdown handler looks the plugin up in a static registry and silently does nothing when it's absent. Handle drags are core, so without this only dragging works. Registry is global, so once at module scope.
OverlayScrollbars.plugin(ClickScrollPlugin)

// Centralized, non-destructive OverlayScrollbars v2 auto-attach (desktop only).
//
// Strategy:
//   - viewport === target: the native scroller IS the viewport, so OS injects NO wrapper divs into React trees (DOM preservation). OS stamps `[data-overlayscrollbars]` on the scroller, which the OS stylesheet gives `position: relative`, so the injected `.os-scrollbar` elements (which are `position: absolute`) always align to the scroller — regardless of the parent's size. Geometry is correct with plain attach everywhere.
//   - By default the bars are appended as the scroller's LAST child, i.e. siblings of its direct children. That is fine for containers whose direct children are arbitrary content, but NOT for list/grid scrollers whose direct children are items targeted by structural/positional selectors. For those, wrap the scroller in a tight `[data-osbar-slot]` element (a positioned box that tightly wraps only the scroller). When the scroller's parent carries that marker we route the bars into it via `scrollbars.slot`, so the bars become siblings of the scroller, never of the items.
//
// Form controls (textarea/input/select) cannot host child divs, so OS can't attach to them; they keep slim native scrollbars (styled in index.scss).

// Marker attribute placed on a tight wrapper around a scroller so the bars land in the wrapper (a sibling of the scroller) instead of inside the scroller itself.
//
// Two independent reasons to use it, both served by the same routing:
//   1. STRUCTURAL scrollers (list/grid) whose direct children are targeted by
//      positional selectors — the bars must not become stray extra children.
//   2. Any scroller where the bar must not RIDE the content. In the app-wide
//      `viewport === target` mode OS appends the bars as children of the SCROLLING element, so they scroll with the content and OS re-pins them each scroll event; that re-pin lags on fast scrolls (wheel or scripted) and the handle visibly slides the wrong way before catching up. Routing the bars to a non-scrolling wrapper via `slot` takes them out of the scroll flow, so they never ride the content and the lag is structurally impossible — while `viewport === target` still holds, so the scroller's own layout (flex, sticky children, etc.) is untouched and it remains the scroll-offset element.
const SLOT_ATTR = 'data-osbar-slot'

// Opt OUT for a subtree: the marked element and its descendants keep NATIVE scrollbars. For surfaces that animate their own scrolling, where an overlay bar is a second representation of a position something else is already moving (see /guides). Such a route restyles the native bars itself — the app-wide hide rule is what makes opting out visible at all.
const SKIP_ATTR = 'data-no-osbar'

// A CUSTOM PROPERTY CANNOT REACH `overflow` OR CONTENT SIZE, and every per-frame writer in the app sets one — the transition bar, a preset cell's fill, the morph scroll, a range thumb. Sound only while no rule reads a `var(--x)` into `overflow` or a size, which none does; re-check if that changes.
const styleDecls = value =>
	(value || '')
		.split(';')
		.map(d => d.trim())
		.filter(d => d && !d.startsWith('--'))
		.sort()
		.join(';')

const overflowReachable = (el, oldValue) => styleDecls(oldValue) !== styleDecls(el.getAttribute('style'))

// No `defer` option here: it belongs to overlayscrollbars-react, not to core, and core silently drops unknown keys. Initialization is deferred by running the scans themselves on idle (see `requestIdle` below).
const OPTIONS = {
	scrollbars: {
		autoHide: 'move', // fade in on pointer move, fade out after idle
		autoHideDelay: 600,
		clickScroll: 'instant',
	},
	update: {
		// ⚠️ OS RE-MEASURES ITS SCROLLER ON EVERY MUTATION INSIDE IT — measured 854ms of forced layout in its own `getElmWidthHeightProperty` over 10s of playback. A custom-property write cannot move content.
		//
		// Both sides must be custom-property-only: `oldValue` is absent unless OS asked for it, and reading an absent one as empty would ignore every style write.
		ignoreMutation: m =>
			m.type === 'attributes' &&
			m.attributeName === 'style' &&
			typeof m.oldValue === 'string' &&
			!styleDecls(m.oldValue) &&
			!styleDecls(m.target?.getAttribute?.('style')),
	},
}

const SKIP_TAGS = new Set(['TEXTAREA', 'INPUT', 'SELECT'])

// OS re-measures only on mutation/scroll; a height transition (Collapsible) ends with none, so it keeps the mid-transition size.
const PAINT_ONLY = new Set([
	'opacity',
	'color',
	'background',
	'background-color',
	'border-color',
	'outline-color',
	'box-shadow',
	'fill',
	'stroke',
	'filter',
	'transform',
	'visibility',
])

// A panel opening ends a transition on each of its children; one forced update per scroller per frame (a load's burst cost 390 ms in one task).
const transitioned = new Set()
const onTransitionEnd = e => {
	if (PAINT_ONLY.has(e.propertyName)) return
	if (!transitioned.size)
		requestAnimationFrame(() => {
			for (const el of transitioned) OverlayScrollbars(el)?.update(true)
			transitioned.clear()
		})
	transitioned.add(e.currentTarget)
}

// Track what we've attached so repeat scans (initial + observer) are cheap.
const attached = new WeakSet()

// Dev only — what a scan actually costs, which is invisible from the console: the walk, the observer records that queued it, and the `getComputedStyle` per node that forces layout. `tests/manual/scrollbarScan.js` reads these. Never in production.
const stats = {
	gcs: 0,
	attachCalls: 0,
	scanRoots: 0,
	scanNodes: 0,
	flushes: 0,
	records: 0,
	scheduledScans: 0,
	scheduledAttaches: 0,
}
if (IS_LOCAL && typeof window !== 'undefined') window.__osStats = stats

const isScrollable = el => {
	if (IS_LOCAL) stats.gcs++
	const { overflowX, overflowY } = getComputedStyle(el)
	// 'overlay' computes as 'auto' in modern Chrome, so it's covered here.
	return overflowX === 'auto' || overflowX === 'scroll' || overflowY === 'auto' || overflowY === 'scroll'
}

// Attach OS to a single element if it's an eligible scroll container.
const attach = el => {
	if (IS_LOCAL) stats.attachCalls++
	if (!el || el.nodeType !== 1) return
	if (SKIP_TAGS.has(el.tagName)) return
	if (!el.parentElement) return
	if (attached.has(el)) return
	// Cheapest discriminator first: the overwhelming majority of elements a scan walks are not scrollers, and bail here before the instance-registry lookup. Every OS-owned element is scrollable, so the `valid()` guard below still runs for them.
	if (!isScrollable(el)) return
	// Opted-out subtree — checked only after `isScrollable`, so this `closest()` runs on the handful of real scrollers rather than on every element a scan walks.
	if (el.closest(`[${SKIP_ATTR}]`)) return
	// Already initialized (e.g. by another scan pass) — skip.
	if (OverlayScrollbars.valid(OverlayScrollbars(el))) {
		attached.add(el)
		return
	}

	// Route bars into a tight wrapper when the scroller's parent is marked — either to keep them out of a structural scroller's child list, or to keep them out of the scroll flow so they don't ride the content (see SLOT_ATTR). Otherwise plain attach (bars as children of the scroller).
	const parent = el.parentElement
	const slot = parent && parent.hasAttribute(SLOT_ATTR) ? parent : undefined

	OverlayScrollbars(
		{
			target: el,
			elements: { viewport: el },
			...(slot ? { scrollbars: { slot } } : {}),
		},
		OPTIONS
	)
	el.addEventListener('transitionend', onTransitionEnd)
	attached.add(el)
}

// Attach to `root` and every descendant element that qualifies.
const scan = root => {
	if (!root || root.nodeType !== 1) return
	attach(root)
	const els = root.querySelectorAll('*')
	if (IS_LOCAL) {
		stats.scanRoots++
		stats.scanNodes += els.length
	}
	for (let i = 0; i < els.length; i++) attach(els[i])
}

// --- Deferred re-scan for later-mounted panels/modals -----------------------
// The MutationObserver fires a lot during live playback, so we coalesce dirty nodes and process them on browser idle rather than synchronously per mutation.

const requestIdle =
	typeof window !== 'undefined' && window.requestIdleCallback
		? window.requestIdleCallback.bind(window)
		: cb => setTimeout(cb, 200)

// Mounted subtrees: walk the whole thing looking for scrollers.
const pendingScan = new Set()
// Restyled elements: only the element itself can have become a scroller.
const pendingAttach = new Set()
let scheduled = false

// Stops at the idle deadline and leaves the rest queued: the dock's first mount scanned in one 130 ms task.
const flush = deadline => {
	if (IS_LOCAL) stats.flushes++
	scheduled = false
	const left = () => !deadline?.timeRemaining || deadline.timeRemaining() > 2
	for (const root of pendingScan) {
		pendingScan.delete(root)
		if (root.nodeType !== 1) continue
		const els = root.querySelectorAll('*')
		if (IS_LOCAL) {
			stats.scanRoots++
			stats.scanNodes += els.length
		}
		pendingAttach.add(root)
		for (const el of els) pendingAttach.add(el)
	}
	for (const el of pendingAttach) {
		if (!left()) return wake()
		pendingAttach.delete(el)
		attach(el)
	}
}

const wake = () => {
	if (scheduled) return
	scheduled = true
	requestIdle(flush)
}

// OverlayScrollbars toggles classes on its own `.os-scrollbar` elements (and their track/handle children) on every scroll frame and every pointer move, which this observer would otherwise feed straight back into a re-scan. None of those elements can ever be a scroller, so drop them before they queue.
const ignored = node => node.nodeType !== 1 || node.closest('.os-scrollbar')

const scheduleScan = node => {
	if (!node || ignored(node)) return
	if (IS_LOCAL) stats.scheduledScans++
	pendingScan.add(node)
	wake()
}

const scheduleAttach = node => {
	if (!node || ignored(node)) return
	if (IS_LOCAL) stats.scheduledAttaches++
	pendingAttach.add(node)
	wake()
}

const onMutations = records => {
	if (IS_LOCAL) stats.records += records.length
	for (const rec of records) {
		if (rec.type === 'childList') {
			rec.addedNodes.forEach(scheduleScan)
		} else if (rec.type === 'attributes') {
			// `overflow` is not inherited, so restyling an element can only make THAT element a scroller — never its descendants. Scanning the whole subtree here would mean a full-document walk every time something sets `document.body.style.cursor` (drag handlers do, twice a drag).
			//
			// The one shape that would break this is an ancestor-gated rule such as `.open .panel { overflow: auto }`. No such rule exists: the only descendant-combinator overflow rule is `Settings .cont [data-page]`, whose ancestor class never changes. Re-check if that changes.
			if (rec.attributeName === 'style' && !overflowReachable(rec.target, rec.oldValue)) continue
			scheduleAttach(rec.target)
		}
	}
}

let observer = null

// Scan the current DOM and watch for later-mounted scroll containers. Call once, after the app has rendered, gated on !IS_MOBILE by the caller.
export const initOsScrollbars = () => {
	if (typeof window === 'undefined' || observer) return
	scan(document.body)
	observer = new MutationObserver(onMutations)
	observer.observe(document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['style', 'class'],
		// `overflowReachable` diffs against the previous value; without this it reads `undefined` and every write looks reachable.
		attributeOldValue: true,
	})
}
